// Pure port of the view-mode / config self-heal that block.coffee ran inline
// (OpenEnded#checkViewMode). The caller resolves everything that needs page state --
// the sentiment and codebook lists, the tag list for the configured codebook, and the
// default view mode -- and this decides which view mode and config actually survive.
//
// resolveViewMode never commits: it returns the resolved pair plus `resetAnswer`,
// which tells the caller the delivered payload no longer matches what will be shown.

// Verbatim `ids` pin the block to another question's answer; anything else is stale.
export const LINKED_IDS_REG = /^[pv]?q(\d+)a(\d+)$/i

export const isValidLinkedIds = ids => LINKED_IDS_REG.test(ids)

export const withDefaultConfig = (config, viewMode, defaultConfig) => {
  const base = defaultConfig?.[viewMode] || {}
  // a valid ids pin outlives the view-mode switch; every other key is re-defaulted
  return Object.hasOwn(config, 'ids') && isValidLinkedIds(config.ids)
    ? Object.assign({ ids: config.ids }, base)
    : Object.assign({}, base)
}

const resolveVerbatim = (config, presetOptions = []) => {
  let resetAnswer = false

  if (Object.hasOwn(config, 'ids') && !isValidLinkedIds(config.ids)) {
    delete config.ids
    resetAnswer = true
  }

  if (Object.hasOwn(config, 'preset') && !presetOptions.some(preset => preset.key === config.preset)) {
    delete config.preset
    resetAnswer = true
  }

  return { config, resetAnswer }
}

// No sentiments left on this question -- fall all the way back to the default mode.
const resolveSentiments = (config, { sentiments, defaultViewMode, defaultConfig }) => {
  if (sentiments.length) return { viewMode: 'sentiments', config, resetAnswer: false }

  return {
    viewMode: defaultViewMode,
    config: withDefaultConfig(config, defaultViewMode, defaultConfig),
    resetAnswer: true
  }
}

// Codebooks present -- keep the configured one if it still carries tags, else take the
// first. No codebooks at all -- same full fallback as sentiments.
const resolveTags = (config, { codeBooks, tags, defaultViewMode, defaultConfig }) => {
  if (!codeBooks.length) {
    return {
      viewMode: defaultViewMode,
      config: withDefaultConfig(config, defaultViewMode, defaultConfig),
      resetAnswer: true
    }
  }

  if (null == config.codebook) {
    config.codebook = codeBooks[0].title
    return { viewMode: 'tags', config, resetAnswer: true }
  }

  if (!tags.length) {
    config.codebook = codeBooks[0].title
    return { viewMode: 'tags', config, resetAnswer: true }
  }

  return { viewMode: 'tags', config, resetAnswer: false }
}

export const resolveViewMode = ({
  viewMode,
  config,
  defaultConfig = {},
  presetOptions = [],
  sentiments = [],
  codeBooks = [],
  tags = [],
  defaultViewMode
}) => {
  // work on a copy -- callers commit the result themselves
  const _config = structuredClone(config || defaultConfig[viewMode] || {})

  if ('verbatim' === viewMode) {
    const { config: resolved, resetAnswer } = resolveVerbatim(_config, presetOptions)
    return { viewMode, config: resolved, resetAnswer }
  }

  if ('sentiments' === viewMode) {
    return resolveSentiments(_config, { sentiments, defaultViewMode, defaultConfig })
  }

  if ('tags' === viewMode) {
    return resolveTags(_config, { codeBooks, tags, defaultViewMode, defaultConfig })
  }

  return { viewMode, config: _config, resetAnswer: false }
}
